import { ExternalLink } from 'lucide-react';
import * as React from 'react';
import { formatCredentialYears } from '../../../helpers/formatCredentialYears';
import { humanize } from '../../../helpers/interestsTaxonomy';
import type { ProfessionalContext } from '../../../interfaces/user.interface';

type SectionKey = 'expertise' | 'education' | 'experience' | 'credentials';

interface Props {
  value: ProfessionalContext;
  onEditSection: (section: SectionKey) => void;
}

const EDUCATION_LABELS: Record<string, string> = {
  HIGH_SCHOOL: 'High School',
  BACHELORS: "Bachelor's",
  MASTERS: "Master's",
  PHD: 'PhD',
  PROFESSIONAL_DEGREE: 'Professional Degree',
  CURRENTLY_ENROLLED: 'Currently Enrolled',
};

const YEARS_LABELS: Record<string, string> = {
  Y0_2: '0–2 years',
  Y3_5: '3–5 years',
  Y6_10: '6–10 years',
  Y11_20: '11–20 years',
  Y20_PLUS: '20+ years',
};

export function ProfessionalContextReadView({
  value,
  onEditSection,
}: Props): React.ReactElement {
  const credentials = React.useMemo(
    () => (Array.isArray(value.credentials) ? value.credentials : []),
    [value.credentials],
  );
  const sortedCredentials = React.useMemo(() => {
    return [...credentials].sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return a.title.localeCompare(b.title);
    });
  }, [credentials]);

  const hasExpertise = value.expertiseAreas.length > 0;
  const hasEducation = Boolean(value.educationLevel);
  const hasExperience =
    Boolean(value.jobTitle) || Boolean(value.yearsExperience);
  const hasCredentials = credentials.length > 0;

  return (
    <div className="plan-c-form__read">
      <section className="plan-c-form__read-section">
        <div className="plan-c-form__read-label">Areas of expertise</div>
        {hasExpertise ? (
          <div className="plan-c-form__read-chips">
            {value.expertiseAreas.map((area) => (
              <span key={area} className="chip chip--read">
                {humanize(area)}
              </span>
            ))}
          </div>
        ) : (
          <button
            type="button"
            className="plan-c-form__read-cta"
            onClick={() => onEditSection('expertise')}
          >
            + Add expertise areas
          </button>
        )}
      </section>

      <section className="plan-c-form__read-section">
        <div className="plan-c-form__read-label">Education</div>
        {hasEducation ? (
          <div className="plan-c-form__read-value">
            {EDUCATION_LABELS[value.educationLevel] ?? value.educationLevel}
            {value.educationLevel === 'CURRENTLY_ENROLLED' &&
              value.fieldOfStudy && <> · {humanize(value.fieldOfStudy)}</>}
            {value.educationLevel === 'CURRENTLY_ENROLLED' &&
              value.institution && <> · {value.institution}</>}
          </div>
        ) : (
          <button
            type="button"
            className="plan-c-form__read-cta"
            onClick={() => onEditSection('education')}
          >
            + Add education info
          </button>
        )}
      </section>

      <section className="plan-c-form__read-section">
        <div className="plan-c-form__read-label">Experience</div>
        {hasExperience ? (
          <div className="plan-c-form__read-value">
            {value.jobTitle}
            {value.jobTitle && value.yearsExperience && ' · '}
            {value.yearsExperience &&
              (YEARS_LABELS[value.yearsExperience] ?? value.yearsExperience)}
          </div>
        ) : (
          <button
            type="button"
            className="plan-c-form__read-cta"
            onClick={() => onEditSection('experience')}
          >
            + Add experience info
          </button>
        )}
      </section>

      <section className="plan-c-form__read-section">
        <div className="plan-c-form__read-label">Credentials</div>
        {hasCredentials ? (
          <ul className="plan-c-form__read-credentials">
            {sortedCredentials.map((cred) => (
              <li key={cred.id} className="plan-c-form__read-credential">
                <span
                  data-testid="credential-row-title"
                  className="plan-c-form__read-credential-title"
                >
                  {cred.title}
                </span>
                <span>
                  {' '}
                  · {cred.issuer} · {formatCredentialYears(cred)}
                </span>
                {cred.link && (
                  <a
                    href={cred.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="External verification link"
                    className="plan-c-form__read-credential-link"
                  >
                    <ExternalLink size={14} />
                  </a>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <button
            type="button"
            className="plan-c-form__read-cta"
            onClick={() => onEditSection('credentials')}
          >
            + Add credentials
          </button>
        )}
      </section>
    </div>
  );
}
